/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";

import { useAuth } from "Authentication/AuthProvider";
import { AddComment } from "Components/AddComment/AddComment";
import { CommentsHistory } from "Components/CommentsHistory/CommentsHistory";
import { OperationStatus } from "Constants/OperationStatus";
import { PermissionsEnum } from "Constants/RolesPermissionsConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { DetailWrapper } from "Containers/Common/CommonContainer.styles";
import { getFlagApplicationStatus } from "Dataflows/Application/ApplicationSelector";
import {
  getAddCommentOperationStatus,
  getComments,
  getCommentsErrorMessage,
  getCommentsIsLoadingStatus,
} from "Dataflows/MVP/Comments/MVPCommentsSelector";
import { addNewComment, fetchUserProfileComments } from "Dataflows/MVP/MVPAction";

import IMostValuableProfessionalCommentsProps from "./IMostValuableProfessionalCommentsProps";

/**
 * The Most Valuable Professional Comments component
 * @method
 * @returns {ReactElement} The Most Valuable Professional Comments component
 */
const MostValuableProfessionalComments = (
  props: IMostValuableProfessionalCommentsProps
): ReactElement => {
  const { isUserLoggedIn, loading, hasAnyRoles, account, hasPermission } = useAuth();
  const { userProfileId } = props;
  const dispatch = useDispatch();
  const addCommentOpStatus = useSelector(getAddCommentOperationStatus);
  const userProfileComments = useSelector(getComments);
  const isLoading = useSelector(getCommentsIsLoadingStatus);
  const errorMessage = useSelector(getCommentsErrorMessage);
  const flagApplicationStatus = useSelector(getFlagApplicationStatus);

  useEffect(() => {
    if (!!userProfileId) {
      dispatch(fetchUserProfileComments(userProfileId));
    }
  }, [
    userProfileId,
    addCommentOpStatus === OperationStatus.Succeeded,
    flagApplicationStatus === OperationStatus.Succeeded,
  ]);

  if (isUserLoggedIn && !hasAnyRoles && !loading) {
    return <Redirect to="/unauthorized" />;
  }

  const onAddCommentClicked = (newComment): void => {
    if (!!userProfileId && !!newComment && !!account.userName) {
      dispatch(addNewComment(userProfileId, newComment, account.userName));
    }
  };

  const canAddComments =
    hasPermission(TenantTypesEnum.MVP, PermissionsEnum.MVP_UPDATE_USERS) ||
    hasPermission(TenantTypesEnum.MVP, PermissionsEnum.MVP_VIEW_APPLICATIONS);

  return (
    <DetailWrapper>
      {canAddComments && (
        <AddComment
          onAddCommentClicked={onAddCommentClicked}
          addCommentStatus={addCommentOpStatus}
        />
      )}
      <CommentsHistory
        userProfileComments={userProfileComments}
        isLoading={isLoading}
        errorMessage={errorMessage}
      />
    </DetailWrapper>
  );
};

export default MostValuableProfessionalComments;
